import Vue from 'vue'
import api from '@/api_alv'
import i18n from '@/assets/i18n/i18n'
import * as Ably from 'ably'

const state = {
    gemaWorksCheckDialogDetailsUsername: "",
    gemaWorksCheckDialogDetailsPassword: "",
    gemaWorksCheckArray: [],
    selectedTagExportDomain: [],
    selectedCsvConversionTool: [],
    dashboardCwrStatusCheckZip: null,

    ably: null,
    channels: {},
    dataSharingNotifications: {},
    dataSharingNotificationsList: [],
    
    // Task Notifications (individual user, background processes)
    taskNotifications: [],
    
    // Running Tasks (live progress tracking)
    runningTasks: {},
    
    contextHelpDialog: {
        show: false,
        context: null,
        section: null
    },

    // Dashboard Quicklinks Guides (PDFs sourced from Storyblok)
    quicklinksGuides: [],
}

const getters = {
    getGemaWorksCheckDialogDetailsUsername          (state) { return state.gemaWorksCheckDialogDetailsUsername },
    getGemaWorksCheckDialogDetailsPassword          (state) { return state.gemaWorksCheckDialogDetailsPassword },
    getGemaWorksCheckArray                          (state) { return state.gemaWorksCheckArray },
    getDashboardDetailsSelectedTagExportDomain      (state) { return state.selectedTagExportDomain  },
    getDashboardDetailsSelectedCsvConversionTool    (state) { return state.selectedCsvConversionTool  },
    getDashboardCwrStatusCheckZip                   (state) { return state.dashboardCwrStatusCheckZip },

    getAbly                                         (state) { return state.ably },
    getChannels                                     (state) { return state.channels },
    getDataSharingNotifications                     (state) { return state.dataSharingNotifications },
    getDataSharingNotificationsList                 (state) { return state.dataSharingNotificationsList },
    
    // Task Notifications
    getTaskNotifications                            (state) { return state.taskNotifications },
    getRunningTasks                                 (state) { return Object.values(state.runningTasks) },
    
    getContextHelpDialog                            (state) { return state.contextHelpDialog },

    // Dashboard Quicklinks Guides
    getQuicklinksGuides                             (state) { return state.quicklinksGuides },
}

const mutations = {
    setGemaWorksCheckDialogDetailsUsername:         (state, value) => { state.gemaWorksCheckDialogDetailsUsername = value },
    setGemaWorksCheckDialogDetailsPassword:         (state, value) => { state.gemaWorksCheckDialogDetailsPassword = value },
    setGemaWorksCheckArray:                         (state, value) => { state.gemaWorksCheckArray = value },
    setDashboardDetailsSelectedTagExportDomain:     (state, value) => { state.selectedTagExportDomain = value },
    setDashboardDetailsSelectedCsvConversionTool:   (state, value) => { state.selectedCsvConversionTool = value },
    setDashboardCwrStatusCheckZip:                  (state, value) => { state.dashboardCwrStatusCheckZip = value },

    setAbly:                                        (state, value) => { state.ably = value },
    setChannels:                                    (state, value) => { state.channels = value },
    setDataSharingNotifications:                    (state, notification) => { state.dataSharingNotifications[notification.id] = notification; state.dataSharingNotificationsList = Object.values(state.dataSharingNotifications); },
    removeDataSharingNotification:                  (state, notificationId) => { delete state.dataSharingNotifications[notificationId]; state.dataSharingNotificationsList = Object.values(state.dataSharingNotifications); },
    resetDataSharingNotifications:                  (state) => { state.dataSharingNotifications = {}; state.dataSharingNotificationsList = []},
    
    // Task Notifications (with LocalStorage persistence)
    addTaskNotification:                            (state, notification) => { 
        state.taskNotifications.unshift(notification)
        localStorage.setItem('alv_task_notifications', JSON.stringify(state.taskNotifications))
    },
    removeTaskNotification:                         (state, notificationId) => { 
        state.taskNotifications = state.taskNotifications.filter(n => n.id !== notificationId)
        localStorage.setItem('alv_task_notifications', JSON.stringify(state.taskNotifications))
    },
    clearTaskNotifications:                         (state) => { 
        state.taskNotifications = []
        localStorage.removeItem('alv_task_notifications')
    },
    loadTaskNotificationsFromStorage:               (state) => {
        const storedNotifications = localStorage.getItem('alv_task_notifications')
        if (storedNotifications) {
            state.taskNotifications = JSON.parse(storedNotifications)
        }
        const storedRunning = localStorage.getItem('alv_running_tasks')
        if (storedRunning) {
            state.runningTasks = JSON.parse(storedRunning)
        }
    },
    
    // Running Tasks (live progress) with LocalStorage persistence
    setRunningTask:                                 (state, task) => { 
        state.runningTasks = { ...state.runningTasks, [task.taskId]: task }
        localStorage.setItem('alv_running_tasks', JSON.stringify(state.runningTasks))
    },
    removeRunningTask:                              (state, taskId) => { 
        const newTasks = { ...state.runningTasks }
        delete newTasks[taskId]
        state.runningTasks = newTasks
        localStorage.setItem('alv_running_tasks', JSON.stringify(state.runningTasks))
    },
    
    setContextHelpDialog:                           (state, payload) => { state.contextHelpDialog = payload },

    setQuicklinksGuides:                            (state, value) => { state.quicklinksGuides = value },
}

const actions = {
    async getGemaIwaWorkToCheckStatus({commit}){
        let params = 'filter=GemaIwaStatusCheck'
        let dir = 'publishing/works'
        
        const r = await api.getFilteredResults(dir, params, "all")

        return r
    },
    async cwrStatusCheck({commit}, payload){
        let metaData = {
            "metadata": payload.metadata,
            "fileName": payload.filename,
        }
        let queryParams = {
            "action": "CwrStatusCheck"
        }
        let dir = 'publishing/works'
        let r = await api.triggerAction(dir, metaData, queryParams)
        return r
    },
    async cwrStatusCheckListOfFiles({commit}){
        // response.<cisaccode-3-char>.metadata|files
        let metaData = {}
        let queryParams = {
            "action": "CwrStatusCheckListOfFiles"
        }
        let dir = 'publishing/works'
        let r = await api.triggerAction(dir, metaData, queryParams)
        return r
    },
    async getGemaWorkStatus({ state }, workId) {
        let metaData = {
            "workIds" : [workId],
            "username" : state.gemaWorksCheckDialogDetailsUsername,
            "password" : state.gemaWorksCheckDialogDetailsPassword
        }

        let queryParams = {
            "action": "GemaIwaStatusCheck"
        }

        let dir = 'publishing/works'
        let r = await api.triggerAction(dir, metaData, queryParams)

        return r
    },
    async gemaIwaLogin({ state }) {
        let metaData = {
            "username" : state.gemaWorksCheckDialogDetailsUsername,
            "password" : state.gemaWorksCheckDialogDetailsPassword
        }

        let queryParams = {
            "action": "GemaIwaLogin"
        }

        let dir = 'publishing/works'
        let r = await api.triggerAction(dir, metaData, queryParams)

        return r
    },
    async tagsExport({ commit, getters }) {
        let metaData = {    "domain" : getters.getDashboardDetailsSelectedTagExportDomain,
                            "language" : i18n.locale
                         }
        let queryParams = { "action" : "tagsExportByDomain" }
        let dir = "dashboard"
        let response = await api.triggerAction(dir, metaData, queryParams)

        if(response.error === -1) {
            api.getFileFromALV(response.response.path)
            commit('setSnackbar', { 
                type: 'success', 
                msg: i18n.t('lang_GENERIC_SNACKBAR_DOWNLOAD_SUCCESSFULL')
            })
        }

        if(response.error === 1) {
            commit('setSnackbar', { 
                type: 'error', 
                msg: i18n.t('lang_GENERIC_SNACKBAR_DOWNLOAD_NOT_SUCCESSFULL')
            })
        }
    },

async csvConversionTool({ commit, getters }, filePath) {
  const tool = getters.getDashboardDetailsSelectedCsvConversionTool
  if (!tool || !filePath) return

  // 1) trigger conversion
  const resp = await api.triggerAction(
    'dashboard',
    { tool, filePath },
    { action: 'csvConversionTools' }
  )


  // 2) on success, resp.response.path is the URL of your generated CSV
  if (resp.error === -1 && resp.response && resp.response.path) {
    // 3) download the CSV
    api.getFileFromALV(resp.response.path)
    commit('setSnackbar', {
      type: 'success',
      msg: i18n.t('lang_GENERIC_SNACKBAR_DOWNLOAD_SUCCESSFULL')
    })
  } else {
    commit('setSnackbar', {
      type: 'error',
      msg: i18n.t('lang_GENERIC_SNACKBAR_DOWNLOAD_NOT_SUCCESSFULL')
    })
  }
},

async fetchDashboardInsights({ commit, getters }, payload = {}) {
    try {
        // Support backwards compatibility: if payload is string, it's the language
        const language = typeof payload === 'string' ? payload : (payload.language || 'de')
        // dateLimitWeeks: null = no limit (all articles), number = weeks back (default 4)
        const dateLimitWeeks = typeof payload === 'object' && payload.dateLimitWeeks !== undefined 
            ? payload.dateLimitWeeks 
            : 4
        
        let queryParams = { "action" : "fetchDashboardInsights" }
        let dir = "dashboard"
        let metaData = { 
            language: language,
            dateLimitWeeks: dateLimitWeeks
        }
        let response = await api.triggerAction(dir, metaData, queryParams)

        if(response.error === -1) {
            return {
                story: response.response.story,
                stories: response.response.stories || [],
                noInterests: response.response.noInterests || false
            }
        }
        
        return null
    } catch (error) {
        console.error('Error fetching dashboard insights:', error)
        return null
    }
},

async fetchDashboardVideos({ commit, getters }, language = 'de') {
    try {
        let queryParams = { "action" : "fetchDashboardVideos" }
        let dir = "dashboard"
        let metaData = { 
            language: language
        }
        let response = await api.triggerAction(dir, metaData, queryParams)

        if(response.error === -1) {
            return {
                story: response.response.story,
                noInterests: response.response.noInterests || false
            }
        }
        
        return null
    } catch (error) {
        console.error('Error fetching dashboard videos:', error)
        return null
    }
},

// Fetch PDF guides from Storyblok for the Dashboard Quicklinks card.
// Returns guides for the requested language (with EN fallback for JA/KO).
// Each guide carries `storyblokDomain` (hyphen format) for nav-order sorting,
// and `pdfUrl` for the download/open action.
async fetchQuicklinksGuides({ commit }, language = 'de') {
    try {
        let queryParams = { "action": "fetchQuicklinksGuides" }
        let dir = "dashboard"
        let metaData = { language: language }
        let response = await api.triggerAction(dir, metaData, queryParams)

        if (response && response.error === -1) {
            const guides = response.response.guides || []
            commit('setQuicklinksGuides', guides)
            return guides
        }

        commit('setQuicklinksGuides', [])
        return []
    } catch (error) {
        console.error('Error fetching quicklinks guides:', error)
        commit('setQuicklinksGuides', [])
        return []
    }
},

    async fetchAblyRequestToken({ commit, getters }) {
        let dir = 'notifications/generateToken'
        try {
            const res = await api.triggerAction(dir, {}, {})
            
            let ably = new Ably.Realtime({
                authCallback: (tokenParams, callback) => {
                    callback(null, res)
                }
            })
            commit('setAbly', ably)
            return true;
        } catch (error) {
            console.error('Failed to connect to Ably: ', error)
            return false;
        }
    },
    subscribeToAblyNotifications({ commit, getters }, callback) {
        let ably = getters.getAbly
        if (!ably) {
            console.error("Notifications service not initialized.")
            return false;
        }

        const accountId = getters.getUser.accountId
        const channelName = `user:${accountId}:data-sharing`

        try {
            let channels = getters.getChannels

            if (!getters.getChannels[channelName]) {
                channels[channelName] = ably.channels.get(channelName)
            }

            channels[channelName].subscribe('notification', (message) => {
                callback(message)
            })

            return true
        } catch (error) {
            console.error('Failed to subscribe to channel: ', error)
            return false
        }
    },
    subscribeToTaskNotifications({ commit, getters }, callback) {
        let ably = getters.getAbly
        if (!ably) {
            console.error("Notifications service not initialized.")
            return false;
        }

        // user.id = ASSIST_ID (individual user), user.accountId = USERID (company)
        const individualUserId = getters.getUser.id
        const channelName = `user:${individualUserId}:tasks`

        try {
            let channels = getters.getChannels

            // Only subscribe once per channel
            if (!channels[channelName]) {
                channels[channelName] = ably.channels.get(channelName)

                channels[channelName].subscribe('task_notification', (message) => {
                    const data = message.data
                    
                    if (data.type === 'task_started' || data.type === 'task_progress') {
                        // Update running task progress
                        commit('setRunningTask', data)
                    } else if (data.type === 'task_complete' || data.type === 'task_failed') {
                        // Remove from running tasks, add to completed notifications
                        if (data.taskId) {
                            commit('removeRunningTask', data.taskId)
                        }
                        commit('addTaskNotification', data)
                        
                        // Show snackbar globally (translate title if it's a lang const)
                        const title = data.title ? i18n.t(data.title) : ''
                        const msg = data.message ? i18n.t(data.message) : ''
                        if (data.type === 'task_complete') {
                            commit('setSnackbar', { type: 'success', msg: `${title}: ${msg}` }, { root: true })
                        } else {
                            commit('setSnackbar', { type: 'error', msg: `${title}: ${msg}` }, { root: true })
                        }
                        
                        if (callback) {
                            callback(data)
                        }
                    }
                })
            }

            return true
        } catch (error) {
            console.error('Failed to subscribe to task channel: ', error)
            return false
        }
    },
    async fetchExistingNotifications({commit, getters}) {
        let res = await api.getExistingNotifications();

        commit('resetDataSharingNotifications')
        
        for (let notification of res) {
            commit('setDataSharingNotifications', notification)
        }
    },
    disconnectAbly({ commit, getters }) {
        if (getters.getAbly) {
            Object.values(getters.getChannels).forEach(channel => {
                channel.unsubcribe()
                channel.detach()
            })

            commit('setChannels', {})
            getters.getAbly.close()
            commit('setAbly', null)
        }
    }
}

export default { state, getters, actions, mutations } 